import { Resolution } from 'src/domains/calculators/utilities/types';
import {
  Coordinates,
  DimensionTextLabels,
  AreaPixelTextLabels,
  RequiredAreaPixelTextLabels,
  PixelTextLabelsCollection,
  CoordinatesSet,
  PixelLabelsCollection,
  DimensionLabelsCollection,
  FramingChartCoordinates,
} from '../types';
import { formatDimensionText, formatPixelText } from './textFormatters';

// Interface for the consolidated dimensions text data
interface DimensionsTextData {
  dimensionLabels: DimensionTextLabels;
  pixelLabels: PixelTextLabelsCollection;
}

// Interface for all label inputs needed from builder
export interface LabelGenerationInputs {
  canvasResolution: Resolution;
  framingDecisionProtection: Resolution;
  framingDecision: Resolution;
  coordinates: FramingChartCoordinates;
  effectiveCanvas: Resolution | null;
  protection: number;
}

// Interface for final label collections
export interface AllLabelsData {
  dimensionsText: DimensionsTextData;
  pixelLabels: PixelLabelsCollection;
  dimensionLabels: DimensionLabelsCollection;
}

/**
 * Get dimension text labels grouped by area
 */
export function getDimensionTextLabels(
  canvasResolution: Resolution,
  effectiveCanvasResolution: Resolution | null,
  framingDecisionProtectionResolution: Resolution,
  framingDecisionResolution: Resolution,
  protection: number
): DimensionTextLabels {
  const canvas = formatDimensionText('Canvas', canvasResolution);

  const effectiveCanvas = effectiveCanvasResolution
    ? formatDimensionText('Effective Canvas', effectiveCanvasResolution)
    : undefined;

  const protectionText =
    protection > 0
      ? formatDimensionText(
          'Framing Decision Protection',
          framingDecisionProtectionResolution
        )
      : undefined;

  const framingDecision = formatDimensionText(
    'Framing Decision',
    framingDecisionResolution
  );

  return {
    canvas,
    effectiveCanvas,
    protection: protectionText,
    framingDecision,
  };
}

export function getCanvasPixelTextLabels(
  canvasCoordinates: Coordinates,
  canvasResolution: Resolution
): RequiredAreaPixelTextLabels {
  const leftText = formatPixelText(canvasCoordinates.x);
  const rightText = formatPixelText(canvasResolution.width - 1);
  const topText = formatPixelText(canvasCoordinates.y);
  const bottomText = formatPixelText(canvasResolution.height - 1);

  return {
    left: leftText,
    right: rightText,
    top: topText,
    bottom: bottomText,
  };
}

export function getProtectionPixelTextLabels(
  protection: number,
  framingDecisionProtectionCoordinates: Coordinates,
  framingDecisionProtectionResolution: Resolution
): AreaPixelTextLabels | undefined {
  if (protection <= 0) {
    return undefined;
  }

  const leftText = formatPixelText(framingDecisionProtectionCoordinates.x);
  const rightText = formatPixelText(
    framingDecisionProtectionCoordinates.x +
      framingDecisionProtectionResolution.width -
      1
  );
  const topText = formatPixelText(framingDecisionProtectionCoordinates.y);
  const bottomText = formatPixelText(
    framingDecisionProtectionCoordinates.y +
      framingDecisionProtectionResolution.height -
      1
  );

  return {
    left: leftText,
    right: rightText,
    top: topText,
    bottom: bottomText,
  };
}

export function getEffectiveCanvasPixelTextLabels(
  effectiveCanvasResolution: Resolution | null,
  effectiveCanvasCoordinates: Coordinates | undefined
): AreaPixelTextLabels | undefined {
  if (!effectiveCanvasResolution || !effectiveCanvasCoordinates) {
    return undefined;
  }

  const leftText = formatPixelText(effectiveCanvasCoordinates.x);
  const rightText = formatPixelText(
    effectiveCanvasCoordinates.x + effectiveCanvasResolution.width - 1
  );
  const topText = formatPixelText(effectiveCanvasCoordinates.y);
  const bottomText = formatPixelText(
    effectiveCanvasCoordinates.y + effectiveCanvasResolution.height - 1
  );

  return {
    left: leftText,
    right: rightText,
    top: topText,
    bottom: bottomText,
  };
}

export function getFramingDecisionPixelTextLabels(
  framingDecisionCoordinates: Coordinates,
  framingDecisionResolution: Resolution
): { right: string; bottom: string } {
  const rightText = formatPixelText(
    framingDecisionCoordinates.x + framingDecisionResolution.width - 1
  );
  const bottomText = formatPixelText(
    framingDecisionCoordinates.y + framingDecisionResolution.height - 1
  );

  return {
    right: rightText,
    bottom: bottomText,
  };
}

export function getAnchorPointPixelTextLabel(anchorPoint: Coordinates): string {
  return `Anchor Point: ${anchorPoint.x}, ${anchorPoint.y}`;
}

export function getPixelTextLabelsCollection(
  canvasCoordinates: Coordinates,
  canvasResolution: Resolution,
  protection: number,
  framingDecisionProtectionCoordinates: Coordinates,
  framingDecisionProtectionResolution: Resolution,
  effectiveCanvasResolution: Resolution | null,
  effectiveCanvasCoordinates: Coordinates | undefined,
  framingDecisionCoordinates: Coordinates,
  framingDecisionResolution: Resolution
): PixelTextLabelsCollection {
  return {
    canvas: getCanvasPixelTextLabels(canvasCoordinates, canvasResolution),
    protection: getProtectionPixelTextLabels(
      protection,
      framingDecisionProtectionCoordinates,
      framingDecisionProtectionResolution
    ),
    effectiveCanvas: getEffectiveCanvasPixelTextLabels(
      effectiveCanvasResolution,
      effectiveCanvasCoordinates
    ),
    framingDecision: getFramingDecisionPixelTextLabels(
      framingDecisionCoordinates,
      framingDecisionResolution
    ),
    anchorPoint: getAnchorPointPixelTextLabel(framingDecisionCoordinates),
  };
}

/**
 * Build grouped pixel labels collection
 */
export function buildPixelLabels(
  textLabels: PixelTextLabelsCollection,
  canvasPixelCoordinates: CoordinatesSet,
  framingDecisionPixelCoordinates: {
    right: Coordinates;
    bottom: Coordinates;
  },
  protectionPixelCoordinates: CoordinatesSet | undefined,
  effectiveCanvasPixelCoordinates: CoordinatesSet | undefined,
  anchorPointPixelCoordinates: Coordinates,
  protection: number,
  effectiveCanvasResolution: Resolution | null
): PixelLabelsCollection {
  return {
    canvas: {
      left: {
        text: textLabels.canvas.left,
        coordinates: canvasPixelCoordinates.left,
      },
      right: {
        text: textLabels.canvas.right,
        coordinates: canvasPixelCoordinates.right,
      },
      top: {
        text: textLabels.canvas.top,
        coordinates: canvasPixelCoordinates.top,
      },
      bottom: {
        text: textLabels.canvas.bottom,
        coordinates: canvasPixelCoordinates.bottom,
      },
    },
    framingDecision: {
      right: {
        text: textLabels.framingDecision.right,
        coordinates: framingDecisionPixelCoordinates.right,
      },
      bottom: {
        text: textLabels.framingDecision.bottom,
        coordinates: framingDecisionPixelCoordinates.bottom,
      },
    },
    protection:
      protection > 0 && textLabels.protection
        ? {
            left:
              textLabels.protection.left && protectionPixelCoordinates
                ? {
                    text: textLabels.protection.left,
                    coordinates: protectionPixelCoordinates.left,
                  }
                : undefined,
            right:
              textLabels.protection.right && protectionPixelCoordinates
                ? {
                    text: textLabels.protection.right,
                    coordinates: protectionPixelCoordinates.right,
                  }
                : undefined,
            top:
              textLabels.protection.top && protectionPixelCoordinates
                ? {
                    text: textLabels.protection.top,
                    coordinates: protectionPixelCoordinates.top,
                  }
                : undefined,
            bottom:
              textLabels.protection.bottom && protectionPixelCoordinates
                ? {
                    text: textLabels.protection.bottom,
                    coordinates: protectionPixelCoordinates.bottom,
                  }
                : undefined,
          }
        : undefined,
    effectiveCanvas:
      effectiveCanvasResolution && textLabels.effectiveCanvas
        ? {
            left:
              textLabels.effectiveCanvas.left && effectiveCanvasPixelCoordinates
                ? {
                    text: textLabels.effectiveCanvas.left,
                    coordinates: effectiveCanvasPixelCoordinates.left,
                  }
                : undefined,
            right:
              textLabels.effectiveCanvas.right &&
              effectiveCanvasPixelCoordinates
                ? {
                    text: textLabels.effectiveCanvas.right,
                    coordinates: effectiveCanvasPixelCoordinates.right,
                  }
                : undefined,
            top:
              textLabels.effectiveCanvas.top && effectiveCanvasPixelCoordinates
                ? {
                    text: textLabels.effectiveCanvas.top,
                    coordinates: effectiveCanvasPixelCoordinates.top,
                  }
                : undefined,
            bottom:
              textLabels.effectiveCanvas.bottom &&
              effectiveCanvasPixelCoordinates
                ? {
                    text: textLabels.effectiveCanvas.bottom,
                    coordinates: effectiveCanvasPixelCoordinates.bottom,
                  }
                : undefined,
          }
        : undefined,
    anchorPoint: {
      text: textLabels.anchorPoint,
      coordinates: anchorPointPixelCoordinates,
    },
  };
}

/**
 * Build grouped dimension labels collection
 */
export function buildDimensionLabels(
  dimensionLabels: DimensionTextLabels,
  canvasDimensionsCoordinates: Coordinates,
  protection: number,
  framingDecisionProtectionDimensionsCoordinates: Coordinates | undefined,
  effectiveCanvasResolution: Resolution | null,
  effectiveCanvasDimensionsCoordinates: Coordinates | undefined,
  framingDecisionDimensionsCoordinates: Coordinates
): DimensionLabelsCollection {
  return {
    canvas: {
      text: dimensionLabels.canvas,
      coordinates: canvasDimensionsCoordinates,
    },
    protection:
      protection > 0 &&
      dimensionLabels.protection &&
      framingDecisionProtectionDimensionsCoordinates
        ? {
            text: dimensionLabels.protection,
            coordinates: framingDecisionProtectionDimensionsCoordinates,
          }
        : undefined,
    effectiveCanvas:
      effectiveCanvasResolution &&
      dimensionLabels.effectiveCanvas &&
      effectiveCanvasDimensionsCoordinates
        ? {
            text: dimensionLabels.effectiveCanvas,
            coordinates: effectiveCanvasDimensionsCoordinates,
          }
        : undefined,
    framingDecision: {
      text: dimensionLabels.framingDecision,
      coordinates: framingDecisionDimensionsCoordinates,
    },
  };
}

/**
 * Generate dimensions text data (extracted from FramingChartBuilder.getDimensionsText)
 */
export function generateDimensionsText(
  canvasResolution: Resolution,
  effectiveCanvasResolution: Resolution | null,
  framingDecisionProtectionResolution: Resolution,
  framingDecisionResolution: Resolution,
  protection: number,
  coordinates: FramingChartCoordinates
): DimensionsTextData {
  const dimensionLabels = getDimensionTextLabels(
    canvasResolution,
    effectiveCanvasResolution,
    framingDecisionProtectionResolution,
    framingDecisionResolution,
    protection
  );

  const pixelLabels = getPixelTextLabelsCollection(
    coordinates.canvasCoordinates,
    canvasResolution,
    protection,
    coordinates.framingDecisionProtectionCoordinates,
    framingDecisionProtectionResolution,
    effectiveCanvasResolution,
    coordinates.effectiveCanvasCoordinates,
    coordinates.framingDecisionCoordinates,
    framingDecisionResolution
  );

  return {
    dimensionLabels,
    pixelLabels,
  };
}

/**
 * Generate pixel labels (extracted from FramingChartBuilder.buildPixelLabels)
 */
export function generatePixelLabels(
  dimensionsText: DimensionsTextData,
  coordinates: FramingChartCoordinates,
  protection: number,
  effectiveCanvasResolution: Resolution | null
): PixelLabelsCollection {
  return buildPixelLabels(
    dimensionsText.pixelLabels,
    coordinates.canvasPixelCoordinates,
    coordinates.framingDecisionPixelCoordinates,
    coordinates.protectionPixelCoordinates,
    coordinates.effectiveCanvasPixelCoordinates,
    coordinates.anchorPointPixelCoordinates,
    protection,
    effectiveCanvasResolution
  );
}

/**
 * Generate dimension labels (extracted from FramingChartBuilder.buildDimensionLabels)
 */
export function generateDimensionLabels(
  dimensionsText: DimensionsTextData,
  coordinates: FramingChartCoordinates,
  protection: number,
  effectiveCanvasResolution: Resolution | null
): DimensionLabelsCollection {
  return buildDimensionLabels(
    dimensionsText.dimensionLabels,
    coordinates.canvasDimensionsCoordinates,
    protection,
    coordinates.framingDecisionProtectionDimensionsCoordinates,
    effectiveCanvasResolution,
    coordinates.effectiveCanvasDimensionsCoordinates,
    coordinates.framingDecisionDimensionsCoordinates
  );
}

/**
 * Generate all labels data needed by FramingChartBuilder
 */
export function generateAllLabels(
  inputs: LabelGenerationInputs
): AllLabelsData {
  const {
    canvasResolution,
    framingDecisionProtection,
    framingDecision,
    coordinates,
    effectiveCanvas,
    protection,
  } = inputs;

  // Generate dimensions text
  const dimensionsText = generateDimensionsText(
    canvasResolution,
    effectiveCanvas,
    framingDecisionProtection,
    framingDecision,
    protection,
    coordinates
  );

  // Generate label collections
  const pixelLabels = generatePixelLabels(
    dimensionsText,
    coordinates,
    protection,
    effectiveCanvas
  );

  const dimensionLabels = generateDimensionLabels(
    dimensionsText,
    coordinates,
    protection,
    effectiveCanvas
  );

  return {
    dimensionsText,
    pixelLabels,
    dimensionLabels,
  };
}
